/**
 * @fileoverview Preview system with error capture and monitoring
 */

import { EventEmitter } from 'eventemitter3'

/**
 * Preview script injection options
 */
export interface PreviewScriptOptions {
  /** Error capture configuration */
  errorCapture?: {
    /** Capture JavaScript errors */
    captureJSErrors?: boolean
    /** Capture console errors */
    captureConsoleErrors?: boolean
    /** Capture network errors */
    captureNetworkErrors?: boolean
    /** Capture resource loading errors */
    captureResourceErrors?: boolean
  }
  /** Port monitoring configuration */
  portMonitoring?: {
    /** Ports to monitor for availability */
    ports?: number[]
    /** Port check interval in milliseconds */
    checkInterval?: number
  }
  /** Custom error handlers */
  customHandlers?: {
    /** Custom error handler script */
    errorHandler?: string
    /** Custom port change handler */
    portHandler?: string
  }
  /** Script injection settings */
  injection?: {
    /** Inject script into HTML pages */
    injectHTML?: boolean
    /** Inject script into service worker */
    injectServiceWorker?: boolean
    /** Script execution position */
    position?: 'head' | 'body' | 'before-body'
  }
}

/**
 * Preview error information
 */
export interface PreviewError {
  /** Error type */
  type: 'javascript' | 'network' | 'resource' | 'console' | 'custom'
  /** Error message */
  message: string
  /** Error source/file */
  source?: string
  /** Line number */
  line?: number
  /** Column number */
  column?: number
  /** Stack trace */
  stack?: string
  /** Additional error details */
  details?: Record<string, any>
  /** Timestamp */
  timestamp: number
  /** URL where error occurred */
  url?: string
}

/**
 * Port change event information
 */
export interface PortChangeEvent {
  /** Port number */
  port: number
  /** Port status */
  status: 'available' | 'unavailable' | 'changed'
  /** Previous port (if changed) */
  previousPort?: number
  /** Timestamp */
  timestamp: number
  /** Service name (if known) */
  service?: string
}

/**
 * Preview manager for error capture and port monitoring
 */
export class PreviewManager extends EventEmitter {
  private httpClient: any
  private sandboxId: string
  private isInjected = false
  private _ready = false
  private monitoringPorts: Set<number> = new Set()

  constructor(httpClient: any, sandboxId: string) {
    super()
    this.httpClient = httpClient
    this.sandboxId = sandboxId
  }

  /**
   * Inject preview script for error capture
   */
  async injectScript(options: PreviewScriptOptions = {}): Promise<void> {
    if (this.isInjected) {
      return
    }

    const script = this.generatePreviewScript(options)

    try {
      const response = await this.httpClient.post(
        `/api/sandbox/${this.sandboxId}/preview/inject`,
        {
          script,
          options: {
            position: options.injection?.position || 'head',
            injectHTML: options.injection?.injectHTML !== false,
            injectServiceWorker: options.injection?.injectServiceWorker || false
          }
        }
      )

      if (!response.success) {
        throw new Error(`Failed to inject preview script: ${response.error}`)
      }

      this.isInjected = true
      this._ready = true

      // Start port monitoring if requested
      if (options.portMonitoring?.ports?.length) {
        this.startPortMonitoring(
          options.portMonitoring.ports,
          options.portMonitoring.checkInterval || 5000
        )
      }

      this.emit('injected')
    } catch (error) {
      throw new Error(`Preview script injection failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Generate the preview script content
   */
  private generatePreviewScript(options: PreviewScriptOptions): string {
    const errorCapture = options.errorCapture || {}
    const customHandlers = options.customHandlers || {}

    return `
(function() {
  'use strict';

  // Error reporting function
  function reportError(error) {
    fetch('${this.getApiBaseUrl()}/api/sandbox/${this.sandboxId}/preview/error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(error)
    }).catch(console.warn);
  }

  // Port change reporting function
  function reportPortChange(portInfo) {
    fetch('${this.getApiBaseUrl()}/api/sandbox/${this.sandboxId}/preview/port-change', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(portInfo)
    }).catch(console.warn);
  }

  ${errorCapture.captureJSErrors !== false ? `
  // JavaScript error capture
  window.addEventListener('error', function(event) {
    reportError({
      type: 'javascript',
      message: event.message,
      source: event.filename,
      line: event.lineno,
      column: event.colno,
      stack: event.error ? event.error.stack : undefined,
      timestamp: Date.now(),
      url: window.location.href
    });
  });

  // Unhandled promise rejection capture
  window.addEventListener('unhandledrejection', function(event) {
    reportError({
      type: 'javascript',
      message: event.reason ? event.reason.toString() : 'Unhandled Promise Rejection',
      stack: event.reason && event.reason.stack ? event.reason.stack : undefined,
      timestamp: Date.now(),
      url: window.location.href,
      details: { type: 'unhandledrejection', reason: event.reason }
    });
  });
  ` : ''}

  ${errorCapture.captureConsoleErrors !== false ? `
  // Console error capture
  const originalConsoleError = console.error;
  console.error = function(...args) {
    reportError({
      type: 'console',
      message: args.map(arg => typeof arg === 'string' ? arg : JSON.stringify(arg)).join(' '),
      timestamp: Date.now(),
      url: window.location.href,
      details: { args: args }
    });
    return originalConsoleError.apply(console, args);
  };
  ` : ''}

  ${errorCapture.captureNetworkErrors !== false ? `
  // Network error capture
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    return originalFetch.apply(this, args).catch(error => {
      reportError({
        type: 'network',
        message: 'Fetch error: ' + error.message,
        timestamp: Date.now(),
        url: window.location.href,
        details: { fetchUrl: args[0], error: error.message }
      });
      throw error;
    });
  };
  ` : ''}

  ${errorCapture.captureResourceErrors !== false ? `
  // Resource loading error capture
  window.addEventListener('error', function(event) {
    if (event.target !== window) {
      const element = event.target;
      reportError({
        type: 'resource',
        message: 'Failed to load resource: ' + (element.src || element.href || 'unknown'),
        timestamp: Date.now(),
        url: window.location.href,
        details: {
          tagName: element.tagName,
          src: element.src,
          href: element.href
        }
      });
    }
  }, true);
  ` : ''}

  ${customHandlers.errorHandler ? customHandlers.errorHandler : ''}

  // Port monitoring (if enabled)
  ${options.portMonitoring?.ports?.length ? this.generatePortMonitoringScript(
    options.portMonitoring.ports,
    options.portMonitoring.checkInterval || 5000,
    customHandlers.portHandler
  ) : ''}

  console.log('[Preview] Error capture and monitoring initialized');
})();
    `.trim()
  }

  /**
   * Generate port monitoring script
   */
  private generatePortMonitoringScript(ports: number[], checkInterval: number, customHandler?: string): string {
    return `
  // Port monitoring
  const monitoredPorts = ${JSON.stringify(ports)};
  const portStatus = new Map();

  async function checkPort(port) {
    try {
      const response = await fetch('http://localhost:' + port + '/health', {
        method: 'HEAD',
        mode: 'no-cors',
        timeout: 1000
      });
      return true;
    } catch {
      return false;
    }
  }

  async function monitorPorts() {
    for (const port of monitoredPorts) {
      const isAvailable = await checkPort(port);
      const previousStatus = portStatus.get(port);

      if (previousStatus !== isAvailable) {
        portStatus.set(port, isAvailable);

        const portInfo = {
          port: port,
          status: isAvailable ? 'available' : 'unavailable',
          timestamp: Date.now(),
          service: getServiceName(port)
        };

        reportPortChange(portInfo);
        ${customHandler ? customHandler + '(portInfo);' : ''}
      }
    }
  }

  function getServiceName(port) {
    const serviceMap = {
      3000: 'api',
      3100: 'api',
      5173: 'dev-server',
      7681: 'terminal',
      8000: 'python-server',
      8080: 'java-server'
    };
    return serviceMap[port] || 'unknown';
  }

  // Start monitoring
  setInterval(monitorPorts, ${checkInterval});
  monitorPorts(); // Initial check
    `
  }

  /**
   * Start port monitoring
   */
  private startPortMonitoring(ports: number[], checkInterval: number): void {
    for (const port of ports) {
      this.monitoringPorts.add(port)
    }

    // Set up WebSocket listener for port change events
    if (this.httpClient.wsClient) {
      this.httpClient.wsClient.on('preview-port-change', (data: PortChangeEvent) => {
        this.emit('port', data.port)
        this.emit('port-change', data)
      })
    }
  }

  /**
   * Handle preview errors from the injected script
   */
  async handlePreviewError(error: PreviewError): Promise<void> {
    this.emit('error', error)

    // Store error in sandbox for debugging
    try {
      await this.httpClient.post(
        `/api/sandbox/${this.sandboxId}/preview/errors`,
        { error }
      )
    } catch (err) {
      console.warn('[Preview] Failed to store error:', err)
    }
  }

  /**
   * Get preview errors from the sandbox
   */
  async getPreviewErrors(limit = 50): Promise<PreviewError[]> {
    try {
      const response = await this.httpClient.get(
        `/api/sandbox/${this.sandboxId}/preview/errors`,
        { limit }
      )

      if (!response.success) {
        throw new Error(`Failed to get preview errors: ${response.error}`)
      }

      return response.data.errors || []
    } catch (error) {
      throw new Error(`Failed to retrieve preview errors: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Clear preview errors
   */
  async clearPreviewErrors(): Promise<void> {
    try {
      const response = await this.httpClient.delete(
        `/api/sandbox/${this.sandboxId}/preview/errors`
      )

      if (!response.success) {
        throw new Error(`Failed to clear preview errors: ${response.error}`)
      }
    } catch (error) {
      throw new Error(`Failed to clear preview errors: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Check if preview script is ready
   */
  get ready(): boolean {
    return this._ready
  }

  /**
   * Get API base URL
   */
  private getApiBaseUrl(): string {
    return this.httpClient.baseURL || ''
  }
}